/**
 * EntityRulesPage — Compliance · Entity Rules.
 *
 * Per-entity-type rules (Individual, JointTenant, Corporation, Trust, Estate, …)
 * that drive the People Form's required fields, signers, docs, CIF mapping, and
 * review. Full create / edit / soft-delete surface, built to match the
 * ProductsPage editor pattern (reload via config read, DataTable with trailing
 * Edit/Delete ghost buttons, a top "New" Card action, a ModalShell form using
 * FormField primitives, a save() handler, and a soft-delete confirm).
 *
 * Reads:  client.retail.config.entityRules()   → ApiResult<ReadonlyArray<EntityRule>>
 * Writes go through the typed bearer-only admin surface (first arg '' = no
 * bypass key — bearer auth carries the request):
 *   client.retail.admin.compliance.entityRules.upsert('', body)
 *   client.retail.admin.compliance.entityRules.remove('', customerType)   // soft delete
 *
 * Option lists for `tinCodeDefault` (S/E/I) and `phase` (Phase1/2/3) are copied
 * verbatim from the legacy editor
 * (apps/ROP-teams/src/modules/Admin/editors/entityRulesEditor.tsx) — both are
 * constrained SelectFields there, so they are SelectFields here too.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { EntityRule } from '@retail-ops/shared-types';
import type { EntityRuleUpsertBody } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, TextArea, SelectField, BoolField, NumberField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

// Phase ENUM — copied verbatim from the legacy editor's PHASES list.
const PHASE_OPTIONS = [
  { value: 'Phase1', label: 'Phase 1 — launches at go-live' },
  { value: 'Phase2', label: 'Phase 2 — follow-on release' },
  { value: 'Phase3', label: 'Phase 3 — future' },
];

// TIN-code ENUM — copied verbatim from the legacy editor's TIN_CODES list.
// "— none —" maps to a null tinCodeDefault.
const TIN_CODE_OPTIONS = [
  { value: '', label: '— none —' },
  { value: 'S', label: 'S — Social Security Number (individuals)' },
  { value: 'E', label: 'E — Employer Identification Number (businesses)' },
  { value: 'I', label: 'I — ITIN (foreign-status individuals)' },
];

interface EditDraft {
  isNew: boolean;
  customerType: string;
  displayName: string;
  description: string;
  callClassCode: string;
  custCode: string;
  tinCodeDefault: string;
  consumerFlag: boolean;
  protectedPerson: boolean;
  businessOnly: boolean;
  personalOnly: boolean;
  phase: string;
  cifMessageTemplate: string;
  nameFormat: string;
  accountTitleFormat: string;
  helpText: string;
  specialInstructions: string;
  openingNotes: string;
  sortOrder: number | null;
  isActive: boolean;
}

function toNum(v: number | string | null | undefined): number | null {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function rowToDraft(r: EntityRule): EditDraft {
  return {
    isNew: false,
    customerType: r.customerType,
    displayName: r.displayName,
    description: r.description ?? '',
    callClassCode: r.callClassCode ?? '',
    custCode: r.custCode ?? '',
    tinCodeDefault: r.tinCodeDefault ?? '',
    consumerFlag: r.consumerFlag ?? false,
    protectedPerson: r.protectedPerson ?? false,
    businessOnly: r.businessOnly ?? false,
    personalOnly: r.personalOnly ?? false,
    phase: r.phase ?? '',
    cifMessageTemplate: r.cifMessageTemplate ?? '',
    nameFormat: r.nameFormat ?? '',
    accountTitleFormat: r.accountTitleFormat ?? '',
    helpText: r.helpText ?? '',
    specialInstructions: r.specialInstructions ?? '',
    openingNotes: r.openingNotes ?? '',
    sortOrder: toNum(r.sortOrder),
    isActive: r.isActive,
  };
}

function blankDraft(): EditDraft {
  return {
    isNew: true,
    customerType: '',
    displayName: '',
    description: '',
    callClassCode: '',
    custCode: '',
    tinCodeDefault: 'S',
    consumerFlag: true,
    protectedPerson: false,
    businessOnly: false,
    personalOnly: true,
    phase: 'Phase1',
    cifMessageTemplate: '',
    nameFormat: '',
    accountTitleFormat: '',
    helpText: '',
    specialInstructions: '',
    openingNotes: '',
    sortOrder: 100,
    isActive: true,
  };
}

function draftToBody(d: EditDraft): EntityRuleUpsertBody {
  const trim = (s: string): string => s.trim();
  const nullable = (s: string): string | null => {
    const t = s.trim();
    return t === '' ? null : t;
  };
  return {
    customerType: trim(d.customerType),
    displayName: trim(d.displayName),
    description: nullable(d.description),
    callClassCode: nullable(d.callClassCode),
    custCode: nullable(d.custCode),
    tinCodeDefault: nullable(d.tinCodeDefault),
    consumerFlag: d.consumerFlag,
    protectedPerson: d.protectedPerson,
    businessOnly: d.businessOnly,
    personalOnly: d.personalOnly,
    phase: nullable(d.phase),
    cifMessageTemplate: nullable(d.cifMessageTemplate),
    nameFormat: nullable(d.nameFormat),
    accountTitleFormat: nullable(d.accountTitleFormat),
    helpText: nullable(d.helpText),
    specialInstructions: nullable(d.specialInstructions),
    openingNotes: nullable(d.openingNotes),
    sortOrder: d.sortOrder,
    isActive: d.isActive,
  };
}

export function EntityRulesPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<EntityRule> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const resp = await client.retail.config.entityRules();
      // The /v3/config/entity-rules endpoint returns data as a versioned
      // envelope { rules: EntityRule[] }, but the api-client types it as the
      // bare array. Accept BOTH shapes so the page loads regardless.
      const raw = resp.data as unknown;
      const rules: EntityRule[] | null = Array.isArray(raw)
        ? (raw as EntityRule[])
        : (raw && typeof raw === 'object' && Array.isArray((raw as { rules?: unknown }).rules)
            ? ((raw as { rules: EntityRule[] }).rules)
            : null);
      if (resp.success && rules) {
        setRows(rules);
      } else {
        setError(resp.error ?? 'Failed to load entity rules.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [client]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const filtered = useMemo(() => {
    if (!rows) return rows;
    if (!filter.trim()) return rows;
    const n = filter.toLowerCase();
    return rows.filter((r) =>
      r.customerType.toLowerCase().includes(n) ||
      r.displayName.toLowerCase().includes(n) ||
      (r.phase ?? '').toLowerCase().includes(n) ||
      (r.custCode ?? '').toLowerCase().includes(n)
    );
  }, [rows, filter]);

  const remove = useCallback(
    async (customerType: string): Promise<void> => {
      if (
        !window.confirm(
          `Deactivate entity rule ${customerType}? It will be soft-deleted (isActive=false) and officers won't be able to open new accounts for this entity type.`
        )
      ) {
        return;
      }
      setError(null);
      try {
        const resp = await client.retail.admin.compliance.entityRules.remove('', customerType);
        if (resp.success === false) {
          setError(resp.error ?? 'Delete failed.');
          return;
        }
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [client, reload],
  );

  const columns: ReadonlyArray<DataTableColumn<EntityRule>> = useMemo(() => [
    {
      header: 'Customer Type',
      cell: (r) => <code className="cell-mono--bold">{r.customerType}</code>,
    },
    { header: 'Display Name', cell: (r) => r.displayName },
    {
      header: 'Phase',
      cell: (r) =>
        r.phase ? <Pill tone="muted">{r.phase}</Pill> : <span className="cell-dim">—</span>,
    },
    {
      header: 'TIN',
      cell: (r) =>
        r.tinCodeDefault ? (
          <code className="cell-mono">{r.tinCodeDefault}</code>
        ) : (
          <span className="cell-dim">—</span>
        ),
    },
    {
      header: 'CustCode',
      cell: (r) =>
        r.custCode ? (
          <code className="cell-mono">{r.custCode}</code>
        ) : (
          <span className="cell-dim">—</span>
        ),
    },
    {
      header: 'Scope',
      cell: (r) =>
        r.businessOnly ? (
          <Pill tone="muted">business</Pill>
        ) : r.personalOnly ? (
          <Pill tone="muted">personal</Pill>
        ) : (
          <span className="cell-dim">—</span>
        ),
    },
    {
      header: 'Status',
      cell: (r) =>
        r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>,
    },
    {
      header: '',
      cell: (r) => (
        <div className="cell-actions">
          <Button size="sm" variant="ghost" onClick={() => setDraft(rowToDraft(r))}>
            Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={() => void remove(r.customerType)}>
            Delete
          </Button>
        </div>
      ),
    },
  ], [remove]);

  const save = useCallback(
    async (d: EditDraft): Promise<void> => {
      setSaving(true);
      setSaveErr(null);
      try {
        const body = draftToBody(d);
        const resp = await client.retail.admin.compliance.entityRules.upsert('', body);
        if (resp.success === false) {
          setSaveErr(resp.error ?? 'Save failed.');
          return;
        }
        setDraft(null);
        await reload();
      } catch (e) {
        setSaveErr(e instanceof Error ? e.message : String(e));
      } finally {
        setSaving(false);
      }
    },
    [client, reload],
  );

  if (error) return <Banner tone="err">Could not load entity rules: {error}</Banner>;
  if (!rows) return <Spinner label="Loading entity rules…" />;

  return (
    <>
      <Card
        title={`Entity Rules — ${filtered?.length ?? 0} of ${rows.length}`}
        actions={
          <div className="users-filter">
            <TextField
              label=""
              value={filter}
              onChange={setFilter}
              placeholder="Filter by customer type, name, phase, or CustCode…"
            />
            <Button onClick={() => setDraft(blankDraft())}>New</Button>
          </div>
        }
      >
        <DataTable<EntityRule>
          columns={columns}
          rows={filtered ?? []}
          rowKey={(r) => r.customerType}
          emptyState={<p className="empty">No entity rules match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={
          draft ? (draft.isNew ? 'New entity rule' : `Edit entity rule — ${draft.customerType}`) : ''
        }
        onClose={() => (saving ? undefined : setDraft(null))}
        width={620}
        actions={
          draft ? (
            <>
              <Button variant="ghost" onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={() => void save(draft)} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : null
        }
      >
        {draft && (
          <>
            {saveErr && <Banner tone="err">{saveErr}</Banner>}
            <TextField
              label="Customer Type"
              value={draft.customerType}
              onChange={(v) => setDraft({ ...draft, customerType: v })}
              disabled={!draft.isNew}
              monospace
              required
              hint={
                draft.isNew
                  ? 'Permanent primary key. Example: Individual, JointTenant, Corporation.'
                  : 'Read-only after create.'
              }
            />
            <TextField
              label="Display Name"
              value={draft.displayName}
              onChange={(v) => setDraft({ ...draft, displayName: v })}
              required
              hint="What officers see in dropdowns."
            />
            <TextArea
              label="Description"
              value={draft.description}
              onChange={(v) => setDraft({ ...draft, description: v })}
              rows={3}
              hint="Plain English. Tells officers what this entity type means."
            />
            <TextField
              label="Call Class Code"
              value={draft.callClassCode}
              onChange={(v) => setDraft({ ...draft, callClassCode: v })}
              monospace
              hint="Core Director call-class code (free-text)."
            />
            <TextField
              label="CustCode"
              value={draft.custCode}
              onChange={(v) => setDraft({ ...draft, custCode: v })}
              monospace
              hint="Core Director CustCode. e.g. Individual, Business."
            />
            <SelectField
              label="Default TIN Type"
              value={draft.tinCodeDefault}
              onChange={(v) => setDraft({ ...draft, tinCodeDefault: v })}
              options={TIN_CODE_OPTIONS}
              hint="Drives which document the People Form asks for first."
            />
            <BoolField
              label="Consumer (CRA-reportable, Reg DD applies)"
              value={draft.consumerFlag}
              onChange={(v) => setDraft({ ...draft, consumerFlag: v })}
            />
            <BoolField
              label="Protected Person (minor, elder, vulnerable adult)"
              value={draft.protectedPerson}
              onChange={(v) => setDraft({ ...draft, protectedPerson: v })}
            />
            <BoolField
              label="Business Only — only business products may be opened"
              value={draft.businessOnly}
              onChange={(v) => setDraft({ ...draft, businessOnly: v })}
            />
            <BoolField
              label="Personal Only — only personal products may be opened"
              value={draft.personalOnly}
              onChange={(v) => setDraft({ ...draft, personalOnly: v })}
            />
            <SelectField
              label="Phase"
              value={draft.phase}
              onChange={(v) => setDraft({ ...draft, phase: v })}
              options={PHASE_OPTIONS}
              placeholder="— No phase —"
              hint="Which release phase this entity type launches in."
            />
            <TextField
              label="CIF Message Template"
              value={draft.cifMessageTemplate}
              onChange={(v) => setDraft({ ...draft, cifMessageTemplate: v })}
            />
            <TextField
              label="Name Format"
              value={draft.nameFormat}
              onChange={(v) => setDraft({ ...draft, nameFormat: v })}
            />
            <TextField
              label="Account Title Format"
              value={draft.accountTitleFormat}
              onChange={(v) => setDraft({ ...draft, accountTitleFormat: v })}
            />
            <TextArea
              label="Help Text"
              value={draft.helpText}
              onChange={(v) => setDraft({ ...draft, helpText: v })}
              rows={2}
              hint="Shown to officers above the People Form when this entity is in play."
            />
            <TextArea
              label="Special Instructions"
              value={draft.specialInstructions}
              onChange={(v) => setDraft({ ...draft, specialInstructions: v })}
              rows={2}
              hint="Extra steps the officer must follow (e.g. notarized affidavit required)."
            />
            <TextArea
              label="Opening Notes"
              value={draft.openingNotes}
              onChange={(v) => setDraft({ ...draft, openingNotes: v })}
              rows={2}
            />
            <NumberField
              label="Sort Order"
              value={draft.sortOrder}
              onChange={(v) => setDraft({ ...draft, sortOrder: v })}
            />
            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
            />
          </>
        )}
      </ModalShell>
    </>
  );
}
